import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartContainer, ChartTitle, EmptyState } from './StatisticsChart.styled';
import { contiOrange } from '../../style/colors';

const COLORS = [contiOrange, '#FF6B35', '#004E89', '#1A659E', '#A7C7E7', '#6B7280'];
const BAR_COLORS = [contiOrange, '#3B82F6', '#8B5CF6', '#10B981', '#EF4444'];

const formatNumber = (value, locale = 'en') => {
  if (typeof value !== 'number') return value;
  return value.toLocaleString(locale, { maximumFractionDigits: 2 });
};

const CustomTooltip = ({ active, payload, label, locale }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: 'white',
          padding: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, marginBottom: '4px', color: '#111827' }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p
            key={index}
            style={{
              margin: 0,
              color: entry.color,
              fontSize: '13px',
            }}
          >
            {entry.name}: {formatNumber(entry.value, locale)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const StatisticsChart = ({
  type,
  data,
  title = null,
  dataKey = 'value',
  xKey = 'name',
  yKey = 'value',
  loading = false,
  multicolor = false,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const yAxisFormatter = (value) => formatNumber(value, locale);

  if (loading) {
    return (
      <ChartContainer>
        {title && <ChartTitle>{title}</ChartTitle>}
        <EmptyState>{t('loading')}...</EmptyState>
      </ChartContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <ChartContainer>
        {title && <ChartTitle>{title}</ChartTitle>}
        <EmptyState>
          <i className="mdi mdi-chart-box-outline" />
          {t('noResults')}
        </EmptyState>
      </ChartContainer>
    );
  }

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey={xKey}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} tickFormatter={yAxisFormatter} />
              <Tooltip content={<CustomTooltip locale={locale} />} />
              <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '12px' }} />
              <Line
                type="monotone"
                dataKey={yKey}
                stroke={contiOrange}
                strokeWidth={3}
                dot={{ fill: contiOrange, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey={xKey}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} tickFormatter={yAxisFormatter} />
              <Tooltip content={<CustomTooltip locale={locale} />} />
              <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '12px' }} />
              <Bar dataKey={yKey} radius={[6, 6, 0, 0]}>
                {multicolor ? (
                  data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                  ))
                ) : (
                  <Cell fill={contiOrange} />
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey={dataKey}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip locale={locale} />} />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return <EmptyState>Unsupported chart type</EmptyState>;
    }
  };

  return (
    <ChartContainer>
      {title && <ChartTitle>{title}</ChartTitle>}
      {renderChart()}
    </ChartContainer>
  );
};

StatisticsChart.propTypes = {
  type: PropTypes.oneOf(['line', 'bar', 'pie']).isRequired,
  data: PropTypes.array.isRequired,
  title: PropTypes.string,
  dataKey: PropTypes.string,
  xKey: PropTypes.string,
  yKey: PropTypes.string,
  loading: PropTypes.bool,
  multicolor: PropTypes.bool,
};

export default StatisticsChart;

